import { useMemo } from "react";
import moment from "moment";
import messageDiff from "./message_diff";

const labels = {
  body: "Message", active: "Status", sms: "Text messages", email: "Email messages",
  send_day: "Send day", send_time: "Send time", send_time_zone: "Time zone",
  reference_point: "Reference point", reference_direction: "Before / after",
  from_email: "Sender email", from_name: "Sender name", lmb: "Last-minute booking",
  min_gap: "Minimum gap", max_gap: "Maximum gap", extend_type: "Extension type",
  bcc: "BCC", options: "Scheduling conditions", tags: "Tags", flags: "Conditions", sources: "Booking sources",
};
const label = (field) => labels[field] || field.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const action = (type) => ({ create: "Created", update: "Updated", remove: "Deleted", delete: "Deleted", restore: "Restored", undelete: "Restored", cancel: "Cancelled" }[type] || label(type || "Changed"));
const format = (value) => {
  if (value == null || value === "" || (Array.isArray(value) && !value.length)) return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((item) => typeof item === "object" ? item.name || item.id || JSON.stringify(item) : item).join(", ");
  if (value?.action && value.fields) return format(value.fields);
  if (typeof value === "object") return Object.entries(value).map(([key, val]) => `${label(key)}: ${format(val)}`).join("\n");
  return String(value);
};
const actor = (log) => `${log.first_name || ""} ${log.last_name || ""}`.trim() || (log.user_id ? `User ${log.user_id}` : "Unknown user");
const date = (value) => value && moment.utc(value).isValid() ? moment.utc(value).local().format("MM/DD/YYYY [at] hh:mm a") : "Unknown time";
const fieldsFor = (log) => Object.entries(log.prev_fields || {}).filter(([field]) => field !== "__after");
const hasChanges = (log) => log.prev_fields && typeof log.prev_fields === "object" && !Array.isArray(log.prev_fields) && fieldsFor(log).length > 0;
const fieldValue = (field, value) => field === "active" && typeof value === "boolean" ? value ? "Enabled" : "Disabled" : format(value);
const hasValue = (record, field) => record != null && Object.prototype.hasOwnProperty.call(record, field) && record[field] !== undefined;
const removedStyle = { background: "#fff1f2", color: "#9f1239", textDecoration: "line-through" };
const addedStyle = { background: "#ecfdf5", color: "#065f46", textDecoration: "underline" };
const CurrentComparison = () => <div className="uk-text-meta" style={{ marginTop: 4 }}>
  Compared with current version · may include later edits
</div>;

const MessageChange = ({ before, after, comparison }) => {
  const sameText = String(before ?? "") === String(after ?? "");
  const spacingOnly = String(before ?? "").replace(/\s+/g, "") === String(after ?? "").replace(/\s+/g, "");
  const changes = messageDiff(before, after);
  return <div>
    <strong>{comparison ? "Message edited" : !after ? "Message cleared" : !before ? "Message added" : spacingOnly ? "Message spacing changed" : "Message edited"}</strong>
    {comparison && <CurrentComparison />}
    {comparison && sameText ? <div>No difference in message text.</div> : <>
      <div style={{ fontSize: 12, margin: "4px 0 6px" }}>
        <span style={removedStyle}>Removed</span>{" · "}<span style={addedStyle}>Added</span>
      </div>
      <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {changes.map(({ type, text }, index) => type === "removed"
          ? <del key={index} style={removedStyle}>{text}</del> : type === "added"
            ? <span key={index}>{changes[index - 1]?.type === "removed" && <span className="uk-text-meta">{" → "}</span>}<ins style={addedStyle}>{text}</ins></span>
            : <span key={index}>{text}</span>)}
      </div>
    </>}
  </div>;
};

const ActivityCard = ({ title, children }) => (
  <article className="uk-card uk-card-default uk-border-rounded" style={{ border: "1px solid var(--border-color, #e5e7eb)", background: "#fff", overflow: "hidden" }}>
    <header className="uk-card-header" style={{ padding: "10px 16px", background: "var(--athens-Gray-light, #f8fafc)", borderBottom: children ? "1px solid var(--border-color, #e5e7eb)" : "none" }}>
      {title}
    </header>
    {children && <div className="uk-card-body" style={{ padding: "10px 16px" }}>{children}</div>}
  </article>
);

const FieldChange = ({ field, before, after, hasAfter, isUpdate, comparison }) => {
  if (!isUpdate) return <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{label(field)}: {fieldValue(field, before)}</div>;
  if (!hasAfter) return <div>{field === "body" ? "Message edited" : `${label(field)} changed`}</div>;
  if (field === "body") return <MessageChange before={before} after={after} comparison={comparison} />;
  if (comparison) return <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
    <div>{label(field)} changed</div>
    <CurrentComparison />
    {fieldValue(field, before) === fieldValue(field, after)
      ? <div>No difference: <strong>{fieldValue(field, after)}</strong>.</div>
      : <div><strong>{fieldValue(field, before)}</strong>{" → "}<strong>{fieldValue(field, after)}</strong> (current).</div>}
  </div>;
  if (typeof after === "boolean" && ["active", "sms", "email", "lmb"].includes(field)) return <div>
    {field === "active" ? `${after ? "Enabled" : "Disabled"} scheduled message` : `${label(field)} ${after ? "enabled" : "disabled"}`}
  </div>;
  return <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
    {label(field)} changed from <strong>{fieldValue(field, before)}</strong> to <strong>{fieldValue(field, after)}</strong>.
  </div>;
};

const RecordedChanges = ({ log, current }) => {
  if (!hasChanges(log)) return null;
  const next = log.prev_fields.__after || {};
  const isUpdate = log.event_type === "update";
  return <div style={{ display: "grid", gap: 12 }}>{fieldsFor(log).map(([field, value]) => {
    const recordedAfter = hasValue(next, field);
    const comparison = isUpdate && !recordedAfter && hasValue(current, field);
    const hasAfter = recordedAfter || comparison;
    let after = recordedAfter ? next[field] : comparison ? current[field] : undefined;
    if (field === "tags" && Array.isArray(value)) return <div key={field}>
      {value.map((tag, index) => <div key={index}>
        {tag.action === "created" ? "Tagged" : tag.action === "remove" ? "Untagged" : "Tag changed"}: {tag.fields?.name || tag.ids?.join(", ") || "Unknown tag"}
      </div>)}
    </div>;
    if (["sources", "flags"].includes(field) && value && typeof value === "object") return <div key={field}>
      <strong>{label(field)}</strong>
      {!!value.created?.length && <div>Added: {format(value.created)}</div>}
      {!!value.removed?.length && <div>Removed: {format(value.removed)}</div>}
    </div>;
    if (field === "options" && value?.fields) return <div style={{ display: "grid", gap: 8 }} key={field}>
      {Object.entries(value.fields).map(([key, previous]) => {
        const recordedOption = recordedAfter && (next[field] === null || hasValue(next[field], key));
        const compareOption = isUpdate && !recordedOption && hasValue(current, field)
          && (current[field] === null || hasValue(current[field], key));
        return <FieldChange key={key} field={key} before={previous}
          after={recordedOption ? next[field]?.[key] ?? null : compareOption ? current[field]?.[key] ?? null : undefined}
          hasAfter={recordedOption || compareOption} isUpdate={isUpdate} comparison={compareOption} />;
      })}
    </div>;
    if (field === "options" && comparison && after) after = Object.fromEntries(
      ["min_gap", "max_gap", "extend_type"].filter((key) => hasValue(after, key)).map((key) => [key, after[key]]),
    );
    return <FieldChange key={field} field={field} before={value} after={after} hasAfter={hasAfter} isUpdate={isUpdate} comparison={comparison} />;
  })}</div>;
};

const SchedulerActivities = ({ logs = [], loading, scheduler = {}, currentScheduler = scheduler, error }) => {
  const visible = useMemo(() => {
    const events = logs.filter((log) => log.event_type !== "create" && (log.event_type !== "update" || hasChanges(log)));
    const created = logs.find((log) => log.event_type === "create");
    if (created) events.push(created);
    else if (scheduler.created_at) events.push({ event_type: "create", date_created: scheduler.created_at, creationFallback: true });
    return events;
  }, [logs, scheduler.created_at]);
  return <div id="scheduler-activities" className="uk-modal-full hms-modals uk-modal uk-open" uk-offcanvas="flip: true; overlay: true">
    <div className="uk-offcanvas-bar"><div className="hms-modals-main">
      <button className="uk-offcanvas-close uk-close-large" type="button" uk-close="true" aria-label="Close activities" />
      <div className="uk-modal-header"><h2 className="uk-modal-title">Activities</h2></div>
      <div className="uk-modal-body uk-margin-top">
        {loading ? <div role="status">Loading activities…</div> : error ? <div role="alert">{error}</div> : <>
          <ul className="uk-list">
            {visible.map((log, index) => <li key={log.id || `${log.date_created}-${index}`} style={{ marginTop: 16 }}>
              <ActivityCard title={`${action(log.event_type)} on ${date(log.date_created)}${log.creationFallback ? "" : ` by ${actor(log)}`}`}>
                {log.event_type !== "create" && hasChanges(log) && <RecordedChanges log={log} current={currentScheduler} />}
              </ActivityCard>
            </li>)}

          </ul>
          {!visible.length && !scheduler.created_at && <div>No activities to display.</div>}
        </>}
      </div>
    </div></div>
  </div>;
};
export default SchedulerActivities;
